import { PlasticsMold } from '../models/plastics-mold-family.model';
import { MoldMaintenanceArea, normalizeMoldCheckupSettings, PlasticsMoldCheckup, PlasticsMoldCheckupSettings } from '../models/plastics-mold-checkup.model';
import { RotoSchedule } from '../models/roto-schedule.model';

/** Arms involved in this changeover; unrelated administrative queue edits may differ from confirmed placement. */
export function moldCheckupTransitionArms(current: RotoSchedule, next: RotoSchedule, updatedTaskIds: string[]): Set<string> {
    const updated = new Set(updatedTaskIds), arms = new Set<string>();
    for (const list of [...current.productLists, ...next.productLists]) {
        const before = current.productLists.find(item => item.id === list.id);
        const after = next.productLists.find(item => item.id === list.id);
        if (before?.moldingTaskIds[0] !== after?.moldingTaskIds[0] || before?.machineId !== after?.machineId || before?.armId !== after?.armId
            || updated.has(list.moldingTaskIds[0])) arms.add(`${list.machineId}/${list.armId}`);
    }
    return arms;
}

export function moldCheckupCycles(mold: PlasticsMold): number {
    return Math.max(0, mold.totalCycleCount - (mold.lastCheckupCycleCount ?? 0));
}
export function moldCheckupInterval(mold: PlasticsMold, settings: PlasticsMoldCheckupSettings): number {
    return mold.checkupInterval ?? settings.defaultInterval;
}
export function isMoldCheckupDue(mold: PlasticsMold, settings: PlasticsMoldCheckupSettings): boolean {
    return mold.checkupOnNextRemoval === true || moldCheckupCycles(mold) >= moldCheckupInterval(mold, settings);
}
export function moldCheckupArea(checkup: PlasticsMoldCheckup): MoldMaintenanceArea { return checkup.maintenanceArea ?? 'preventative'; }
export function moldCheckupDropoff(checkup: PlasticsMoldCheckup, settings: PlasticsMoldCheckupSettings): { english: string; spanish: string } {
    const value = normalizeMoldCheckupSettings(settings);
    return moldCheckupArea(checkup) === 'general'
        ? { english: value.generalEnglish, spanish: value.generalSpanish }
        : { english: value.preventativeEnglish, spanish: value.preventativeSpanish };
}

export function validateMoldCheckupSettings(settings: PlasticsMoldCheckupSettings): void {
    if (!Number.isSafeInteger(settings.defaultInterval) || settings.defaultInterval <= 0) throw new Error('Cycle interval must be a positive whole number.');
    for (const definitions of [settings.checklist, settings.fabricationReasons, settings.noticeTags]) {
        if (!Array.isArray(definitions) || new Set(definitions.map(item => item.id)).size !== definitions.length
            || definitions.some(item => !/^[A-Za-z0-9]{20}$/.test(item.id) || !item.label.trim() || item.label.length > 500)) {
            throw new Error('Every checklist item, reason, and notice tag needs a unique ID and a label of at most 500 characters.');
        }
    }
    const value = normalizeMoldCheckupSettings(settings);
    if ([value.preventativeEnglish, value.preventativeSpanish, value.generalEnglish, value.generalSpanish].some(direction =>
        typeof direction !== 'string' || !direction.trim() || direction.length > 1000)) throw new Error('Provide maintenance-area directions in both languages (up to 1000 characters).');
    if (settings.enabled && !settings.checklist.some(item => !item.archived)) {
        throw new Error('Configure a checklist before enabling automatic mold checkups.');
    }
}
